// ---------------------------------------------------------------------------
// Test Mode — the Meetings module (Întâlniri): the directory of external
// people we have met, and the log of meetings with them. Contacts hang off the
// simulated clients and meetings off the simulated projects, so a meeting's
// "Client / Proiect" column and a contact's "Client" link land on real demo
// pages.
// ---------------------------------------------------------------------------

import type {
  Meeting,
  MeetingContact,
  MeetingContactDetail,
  MeetingFilters,
  MeetingNoteSummary,
  PageMeta,
} from '../lib/meetingLogApi';
import type { MeetingNote } from '../lib/meetingNotesApi';
import { CLIENT_SEEDS } from './clients';
import { isoDateTime } from './company';
import { PEOPLE_REFS } from './people';
import { PROJECT_SEEDS } from './projects';

interface ContactSeed {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  companyName: string | null;
  role: string | null;
  clientId: string | null;
  notes?: string;
}

const CONTACT_SEEDS: ContactSeed[] = [
  {
    id: 'mc-1',
    firstName: 'Elena',
    lastName: 'Marinescu',
    email: 'elena.marinescu@fashionro.ro',
    phone: '+40 721 334 556',
    companyName: 'FashionRO SRL',
    role: 'Head of E-commerce',
    clientId: 'c1',
    notes: 'Decident pe partea de checkout; preferă întâlniri joia.',
  },
  {
    id: 'mc-2',
    firstName: 'Radu',
    lastName: 'Ionescu',
    email: 'radu.ionescu@fintechpartners.ro',
    phone: '+40 733 118 902',
    companyName: 'FinTech Partners SA',
    role: 'CTO',
    clientId: 'c2',
  },
  {
    id: 'mc-3',
    firstName: 'Ioana',
    lastName: 'Dumitrescu',
    email: 'ioana.d@fintechpartners.ro',
    phone: null,
    companyName: 'FinTech Partners SA',
    role: 'Product Owner',
    clientId: 'c2',
  },
  {
    id: 'mc-4',
    firstName: 'Mihai',
    lastName: 'Constantin',
    email: 'mihai.constantin@dataviz.ro',
    phone: '+40 745 667 210',
    companyName: 'DataViz Corp SRL',
    role: 'CEO',
    clientId: 'c3',
  },
  {
    id: 'mc-5',
    firstName: 'Cristina',
    lastName: 'Popa',
    email: 'cristina.popa@megaindustries.ro',
    phone: '+40 722 909 133',
    companyName: 'Mega Industries SRL',
    role: 'Director operațiuni',
    clientId: 'c4',
  },
  {
    id: 'mc-6',
    firstName: 'Bogdan',
    lastName: 'Enache',
    email: 'bogdan@logitrans.ro',
    phone: '+40 731 555 018',
    companyName: 'LogiTrans SA',
    role: 'IT Manager',
    clientId: 'c5',
  },
  {
    id: 'mc-7',
    firstName: 'Ana',
    lastName: 'Stanciu',
    email: 'ana.stanciu@peoplefirst.ro',
    phone: null,
    companyName: 'PeopleFirst SRL',
    role: 'HR Director',
    clientId: 'c6',
  },
  {
    id: 'mc-8',
    firstName: 'Dan',
    lastName: 'Vlad',
    email: 'dan.vlad@industriaplus.ro',
    phone: '+40 740 202 771',
    companyName: 'IndustriaPlus',
    role: 'Director general',
    clientId: null,
    notes: 'Lead — Integrare ERP. Vine din recomandarea Mega Industries.',
  },
  {
    id: 'mc-9',
    firstName: 'Laura',
    lastName: 'Georgescu',
    email: 'laura@greenbuild.ro',
    phone: '+40 728 414 990',
    companyName: 'GreenBuild',
    role: 'Fondator',
    clientId: null,
  },
];

interface MeetingSeed {
  id: string;
  title: string;
  /** Days from today; negative = past. */
  dayOffset: number;
  hour: number;
  durationMinutes: number;
  location: string | null;
  meetingType: string;
  ownerId: string;
  teamMemberIds: string[];
  attendeeIds: string[];
  projectId: string | null;
  notes: string | null;
  /** Whether a transcript/AI note exists for it. */
  withNote?: 'COMPLETED' | 'PROCESSING';
}

const MEETING_SEEDS: MeetingSeed[] = [
  {
    id: 'mt-1',
    title: 'Kickoff redesign checkout',
    dayOffset: -21,
    hour: 10,
    durationMinutes: 60,
    location: 'Google Meet',
    meetingType: 'KICKOFF',
    ownerId: 'p1',
    teamMemberIds: ['p7', 'p9'],
    attendeeIds: ['mc-1'],
    projectId: 'pr1',
    notes: 'Am agreat scope-ul fazei 1 și data de lansare.',
    withNote: 'COMPLETED',
  },
  {
    id: 'mt-2',
    title: 'Status săptămânal Mobile Banking',
    dayOffset: -7,
    hour: 11,
    durationMinutes: 30,
    location: 'Google Meet',
    meetingType: 'STATUS',
    ownerId: 'p3',
    teamMemberIds: ['p8'],
    attendeeIds: ['mc-2', 'mc-3'],
    projectId: 'pr2',
    notes: null,
    withNote: 'COMPLETED',
  },
  {
    id: 'mt-3',
    title: 'Discovery — dashboard BI',
    dayOffset: -5,
    hour: 14,
    durationMinutes: 90,
    location: 'Sediul DataViz, Cluj',
    meetingType: 'DISCOVERY',
    ownerId: 'p1',
    teamMemberIds: ['p2', 'p5'],
    attendeeIds: ['mc-4'],
    projectId: 'pr3',
    notes: 'Au nevoie de rapoarte pe vânzări și stocuri; integrare cu SAP.',
    withNote: 'COMPLETED',
  },
  {
    id: 'mt-4',
    title: 'Negociere contract Integrare ERP',
    dayOffset: -2,
    hour: 16,
    durationMinutes: 60,
    location: 'Telefon',
    meetingType: 'NEGOTIATION',
    ownerId: 'p1',
    teamMemberIds: [],
    attendeeIds: ['mc-8'],
    projectId: null,
    notes: 'Cer o reducere de 10% pentru plata în avans. Revenim cu o ofertă revizuită.',
    withNote: 'PROCESSING',
  },
  {
    id: 'mt-5',
    title: 'Review sprint 14 — Mobile Banking',
    dayOffset: -1,
    hour: 15,
    durationMinutes: 45,
    location: 'Google Meet',
    meetingType: 'REVIEW',
    ownerId: 'p3',
    teamMemberIds: ['p8', 'p10'],
    attendeeIds: ['mc-3'],
    projectId: 'pr2',
    notes: null,
  },
  {
    id: 'mt-6',
    title: 'Status Mega Industries — modul depozit',
    dayOffset: 0,
    hour: 13,
    durationMinutes: 30,
    location: 'Google Meet',
    meetingType: 'STATUS',
    ownerId: 'p4',
    teamMemberIds: ['p11'],
    attendeeIds: ['mc-5'],
    projectId: 'pr4',
    notes: null,
  },
  {
    id: 'mt-7',
    title: 'Demo GreenBuild — website de prezentare',
    dayOffset: 1,
    hour: 11,
    durationMinutes: 45,
    location: 'Google Meet',
    meetingType: 'DEMO',
    ownerId: 'p1',
    teamMemberIds: ['p6'],
    attendeeIds: ['mc-9'],
    projectId: null,
    notes: 'Lead din pipeline (LEAD-3). Pregătim un mock în Figma.',
  },
  {
    id: 'mt-8',
    title: 'Planificare Q4 — LogiTrans',
    dayOffset: 3,
    hour: 10,
    durationMinutes: 120,
    location: 'Sediul LogiTrans, București',
    meetingType: 'PLANNING',
    ownerId: 'p2',
    teamMemberIds: ['p13', 'p14'],
    attendeeIds: ['mc-6'],
    projectId: 'pr5',
    notes: null,
  },
  {
    id: 'mt-9',
    title: 'Onboarding HR Management System',
    dayOffset: 6,
    hour: 9,
    durationMinutes: 60,
    location: 'Google Meet',
    meetingType: 'ONBOARDING',
    ownerId: 'p3',
    teamMemberIds: ['p9'],
    attendeeIds: ['mc-7'],
    projectId: 'pr6',
    notes: null,
  },
  {
    id: 'mt-10',
    title: 'Retrospectivă fază 1 — checkout',
    dayOffset: 9,
    hour: 14,
    durationMinutes: 60,
    location: 'Birou CCC',
    meetingType: 'RETROSPECTIVE',
    ownerId: 'p1',
    teamMemberIds: ['p7', 'p9', 'p6'],
    attendeeIds: ['mc-1'],
    projectId: 'pr1',
    notes: null,
  },
];

function clientRef(clientId: string | null): { id: string; name: string } | null {
  if (!clientId) return null;
  const client = CLIENT_SEEDS.find((c) => c.id === clientId);
  return client ? { id: client.id, name: client.name } : null;
}

function contactFromSeed(seed: ContactSeed): MeetingContact {
  return {
    id: seed.id,
    firstName: seed.firstName,
    lastName: seed.lastName,
    fullName: `${seed.firstName} ${seed.lastName}`,
    email: seed.email,
    phone: seed.phone,
    companyName: seed.companyName,
    role: seed.role,
    notes: seed.notes ?? null,
    clientId: seed.clientId,
    client: clientRef(seed.clientId),
    companyId: null,
    createdAt: isoDateTime(-120, 10),
    updatedAt: isoDateTime(-3, 12),
  };
}

function personRef(id: string) {
  return PEOPLE_REFS.find((p) => p.id === id) ?? { id, name: 'Necunoscut' };
}

function noteFor(seed: MeetingSeed): MeetingNoteSummary | null {
  if (!seed.withNote) return null;
  const done = seed.withNote === 'COMPLETED';
  return {
    id: `${seed.id}-note`,
    title: `Transcript — ${seed.title}`,
    date: isoDateTime(seed.dayOffset, seed.hour),
    source: 'recording',
    ingestStatus: seed.withNote,
    summary: done
      ? 'Discuție despre obiectivele fazei curente, riscurile identificate și pașii următori agreați de ambele părți.'
      : null,
    decisions: done
      ? '- Livrarea fazei 1 rămâne pe data agreată\n- Testarea se face în staging'
      : null,
    actionItems: done
      ? '- CCC trimite planul detaliat până vineri\n- Clientul confirmă lista de utilizatori pilot'
      : null,
    keyFacts: done ? '- Buget aprobat pentru faza 1\n- Lansare estimată în 6 săptămâni' : null,
    openQuestions: done ? '- Integrarea cu sistemul de facturare intră în faza 1 sau 2?' : null,
    updatedAt: isoDateTime(seed.dayOffset, seed.hour + 2),
  };
}

function meetingFromSeed(seed: MeetingSeed): Meeting {
  const owner = personRef(seed.ownerId);
  const project = seed.projectId ? PROJECT_SEEDS.find((p) => p.id === seed.projectId) : null;
  const attendees = seed.attendeeIds
    .map((id) => CONTACT_SEEDS.find((c) => c.id === id))
    .filter((c): c is ContactSeed => Boolean(c))
    .map(contactFromSeed);
  // The client is the project's client, or the first attendee's.
  const clientId = project?.clientId ?? attendees[0]?.clientId ?? null;
  const note = noteFor(seed);
  const start = new Date(isoDateTime(seed.dayOffset, seed.hour));
  const end = new Date(start.getTime() + seed.durationMinutes * 60_000);
  return {
    id: seed.id,
    title: seed.title,
    startAt: start.toISOString(),
    endAt: end.toISOString(),
    location: seed.location,
    meetingType: seed.meetingType,
    notes: seed.notes,
    companyId: null,
    company: null,
    owner: {
      id: owner.id,
      name: owner.name,
      email: `${owner.name.toLowerCase().replace(/\s+/g, '.')}@cleancodeculture.ro`,
      avatarUrl: null,
    },
    createdBy: { id: owner.id, name: owner.name },
    client: clientRef(clientId),
    project: project ? { id: project.id, name: project.name, code: project.code } : null,
    lead: null,
    attendees,
    teamMembers: seed.teamMemberIds.map((id) => {
      const p = personRef(id);
      return { id: p.id, fullName: p.name, email: null, userId: p.id, avatarUrl: null };
    }),
    access: [],
    notesCount: note ? 1 : 0,
    latestNote: note,
    meetingNotes: note ? [note] : [],
    createdAt: isoDateTime(Math.min(seed.dayOffset - 3, -1), 9),
    updatedAt: isoDateTime(Math.min(seed.dayOffset, 0), 17),
  };
}

let meetingsCache: Meeting[] | null = null;
function allMeetings(): Meeting[] {
  if (!meetingsCache) meetingsCache = MEETING_SEEDS.map(meetingFromSeed);
  return meetingsCache;
}

export function buildMeeting(id: string): Meeting | null {
  return allMeetings().find((m) => m.id === id) ?? null;
}

/** The full note record the meeting detail page lists under "Transcriere și notițe AI". */
function fullNote(meeting: Meeting, summary: MeetingNoteSummary): MeetingNote {
  const done = summary.ingestStatus === 'COMPLETED';
  return {
    id: summary.id,
    clientId: meeting.client?.id ?? null,
    projectId: meeting.project?.id ?? null,
    leadId: null,
    personId: null,
    meetingId: meeting.id,
    title: summary.title,
    date: summary.date,
    attendees: meeting.attendees.map((a) => a.fullName).join(', ') || null,
    meetingType: meeting.meetingType,
    content: done
      ? `Transcriere (simulată)\n\n${meeting.owner.name}: Mulțumim că ați acceptat întâlnirea. Propun să trecem prin obiectivele fazei curente.\n${meeting.attendees[0]?.fullName ?? 'Client'}: Perfect. Pentru noi prioritatea este lansarea în termen și integrarea cu sistemele existente.\n${meeting.owner.name}: Am notat. Trimitem planul detaliat până vineri și confirmăm lista de utilizatori pilot.`
      : 'Transcriere în curs de procesare…',
    summary: summary.summary,
    decisions: summary.decisions,
    actionItems: summary.actionItems,
    keyFacts: summary.keyFacts,
    openQuestions: summary.openQuestions,
    source: summary.source,
    s3Key: null,
    s3Url: null,
    audioKey: null,
    ingestStatus: done ? 'COMPLETED' : 'PROCESSING',
    ingestLog: null,
    lastIngestedAt: done ? summary.updatedAt : null,
    isIndexed: done,
    createdById: meeting.owner.id,
    createdAt: summary.date,
    updatedAt: summary.updatedAt,
  };
}

export function meetingNotesForMeeting(meetingId: string): MeetingNote[] {
  const meeting = buildMeeting(meetingId);
  if (!meeting) return [];
  return meeting.meetingNotes.map((n) => fullNote(meeting, n));
}

export function buildMeetingLogNote(noteId: string): MeetingNote | null {
  for (const meeting of allMeetings()) {
    const summary = meeting.meetingNotes.find((n) => n.id === noteId);
    if (summary) return fullNote(meeting, summary);
  }
  return null;
}

/** The list endpoint: newest first, every filter the page can send honoured. */
export function buildMeetings(filters: Partial<MeetingFilters>): {
  data: Meeting[];
  meta: PageMeta;
} {
  let items = allMeetings();
  const q = filters.search?.trim().toLowerCase();
  if (q) {
    items = items.filter(
      (m) =>
        m.title.toLowerCase().includes(q) ||
        m.attendees.some(
          (a) =>
            a.fullName.toLowerCase().includes(q) || (a.companyName ?? '').toLowerCase().includes(q),
        ),
    );
  }
  if (filters.clientId) items = items.filter((m) => m.client?.id === filters.clientId);
  if (filters.projectId) items = items.filter((m) => m.project?.id === filters.projectId);
  if (filters.ownerId) items = items.filter((m) => m.owner.id === filters.ownerId);
  if (filters.contactId) {
    items = items.filter((m) => m.attendees.some((a) => a.id === filters.contactId));
  }
  if (filters.from) items = items.filter((m) => m.startAt.slice(0, 10) >= filters.from!);
  if (filters.to) items = items.filter((m) => m.startAt.slice(0, 10) <= filters.to!);
  const asc = filters.sort === 'startAt';
  items = [...items].sort((a, b) =>
    asc ? a.startAt.localeCompare(b.startAt) : b.startAt.localeCompare(a.startAt),
  );
  const limit = filters.limit ?? 20;
  const page = filters.page ?? 1;
  const total = items.length;
  return {
    data: items.slice((page - 1) * limit, page * limit),
    meta: { page, limit, total, totalPages: Math.max(1, Math.ceil(total / limit)) },
  };
}

export function buildMeetingContacts(search?: string | null): {
  data: MeetingContact[];
  meta: PageMeta;
} {
  const q = search?.trim().toLowerCase();
  let items = CONTACT_SEEDS.map((seed) => ({
    ...contactFromSeed(seed),
    meetingsCount: allMeetings().filter((m) => m.attendees.some((a) => a.id === seed.id)).length,
  }));
  if (q) {
    items = items.filter(
      (c) =>
        c.fullName.toLowerCase().includes(q) ||
        (c.companyName ?? '').toLowerCase().includes(q) ||
        (c.email ?? '').toLowerCase().includes(q),
    );
  }
  items.sort((a, b) => a.fullName.localeCompare(b.fullName));
  return {
    data: items,
    meta: { page: 1, limit: 50, total: items.length, totalPages: 1 },
  };
}

export function buildMeetingContact(id: string): MeetingContactDetail | null {
  const seed = CONTACT_SEEDS.find((c) => c.id === id);
  if (!seed) return null;
  const meetings = allMeetings()
    .filter((m) => m.attendees.some((a) => a.id === id))
    .map((m) => ({
      id: m.id,
      title: m.title,
      startAt: m.startAt,
      owner: { id: m.owner.id, name: m.owner.name },
      client: m.client,
      project: m.project,
      lead: m.lead,
    }));
  return { ...contactFromSeed(seed), meetingsCount: meetings.length, meetings };
}
